import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Height (px) that makes an element run from its own top edge to the bottom of
 * the viewport — for grids and boards that should end at the bottom of the
 * screen rather than at an arbitrary fixed height.
 *
 * Returns [ref, height]. `ref` is a CALLBACK ref: attaching it measures
 * immediately, which matters because these surfaces usually mount later than
 * the screen does (a placeholder renders until the fetch lands), and a plain ref
 * would leave the height at its floor with nothing to trigger a re-measure.
 */
export const useFillViewportHeight = ({
  minHeight = 320,
  bottomGap = 24,
} = {}) => {
  const nodeRef = useRef(null);
  const observerRef = useRef(null);
  const [height, setHeight] = useState(minHeight);

  const measure = useCallback(() => {
    const el = nodeRef.current;
    if (!el) return;
    // Viewport-relative, NOT document-relative: measuring from the top of the
    // document only matches the visible gap while the page sits at scroll 0, so
    // measuring mid-scroll sized the element as if the scrolled-away pixels
    // were still above it.
    //
    // Clamped at 0 for the case where the element's top is above the viewport:
    // fill the whole window rather than adding the off-screen part back.
    const top = Math.max(el.getBoundingClientRect().top, 0);
    const next = Math.max(
      minHeight,
      Math.round(window.innerHeight - top - bottomGap),
    );
    // Ignore sub-pixel churn so the observers below can't ping-pong.
    setHeight((prev) => (Math.abs(prev - next) > 1 ? next : prev));
  }, [minHeight, bottomGap]);

  const ref = useCallback(
    (node) => {
      nodeRef.current = node;
      if (observerRef.current) observerRef.current.disconnect();
      if (!node) return;

      measure();

      if (typeof ResizeObserver === "undefined") return;
      const observer = new ResizeObserver(measure);
      observerRef.current = observer;
      // The element itself, and its parent because content ABOVE the element
      // moves its top edge without changing its own size. `document.body` is
      // useless here — the dashboard pins it to 100vh, so it never resizes.
      observer.observe(node);
      if (node.parentElement) observer.observe(node.parentElement);
    },
    [measure],
  );

  useEffect(() => {
    window.addEventListener("resize", measure);
    // Self-corrects when the element mounts while the page is still scrolled
    // (e.g. switching views after scrolling). Once sized, the page fits the
    // window and there is nothing left to scroll, so this stays quiet.
    window.addEventListener("scroll", measure, { passive: true });

    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure);
    };
  }, [measure]);

  useEffect(
    () => () => {
      if (observerRef.current) observerRef.current.disconnect();
    },
    [],
  );

  return [ref, height];
};

export default useFillViewportHeight;
